<template>
  <v-dialog
    :model-value="isOpen"
    max-width="460"
    @update:model-value="(v) => { if (!v) onCancel(); }"
  >
    <v-card v-if="pending">
      <v-card-title class="text-body-1 font-weight-medium">
        {{ $gt('Anruf vorbereiten') }}
      </v-card-title>
      <v-card-text>
        <p class="text-body-2 mb-2">
          <strong>{{ pending.contactLabel || pending.to }}</strong>
          <span
            v-if="pending.contactLabel && pending.to && pending.contactLabel !== pending.to"
            class="text-medium-emphasis"
          >
            · {{ pending.to }}
          </span>
        </p>
        <p class="text-caption text-medium-emphasis mb-3">
          {{ $gt('Ihr Telefon klingelt zuerst – sobald Sie abnehmen, wird der Kontakt verbunden.') }}
        </p>
        <!-- Kundenfeedback 30.10.2026 (I): Rufnummer je Anruf wählen, Favorit
             speichern, Link zur zentralen Einstellung. -->
        <section v-if="callerOptions" class="mb-3" aria-labelledby="call-caller-id-title">
          <p id="call-caller-id-title" class="text-body-2 font-weight-medium mb-1">
            {{ $gt('Angezeigte Rufnummer') }}
          </p>
          <template v-if="callerOptions.hasCompanyNumber">
            <v-radio-group
              v-model="callerIdMode"
              aria-labelledby="call-caller-id-title"
              color="primary"
              density="compact"
              hide-details
            >
              <v-radio
                v-for="option in callerOptions.options"
                :key="option.mode"
                :label="`${option.mode === 'company' ? $gt('Geschäftsnummer') : $gt('KARS-Rufnummer')} · ${option.number}`"
                :value="option.mode"
              />
            </v-radio-group>
            <v-checkbox
              color="primary"
              density="compact"
              :disabled="isFavorite"
              hide-details
              :label="$gt('Als meinen Favoriten speichern')"
              :model-value="isFavorite || saveAsFavorite"
              @update:model-value="(v) => { saveAsFavorite = !!v; }"
            />
            <!-- KARS kennt den Freischaltstatus beim Anbieter nicht, daher der
                 Hinweis bei jeder Wahl der Geschäftsnummer. -->
            <p v-if="callerIdMode === 'company'" class="text-caption text-medium-emphasis mb-1">
              {{ $gt('Die Geschäftsnummer wird nur angezeigt, wenn sie beim Telefonie-Anbieter freigeschaltet ist.') }}
            </p>
            <a
              v-if="centralSettingsHref"
              class="custom-link text-caption"
              :href="centralSettingsHref"
              rel="noopener"
              target="_blank"
            >{{ $gt('Zentrale Einstellung') }}</a>
          </template>
          <template v-else>
            <p class="text-body-2 mb-1">
              {{ $gt('KARS-Rufnummer') }} · {{ callerOptions.options[0]?.number }}
            </p>
            <p class="text-caption text-medium-emphasis mb-0">
              {{ $gt('Für dieses Unternehmen ist keine Geschäftsnummer hinterlegt.') }}
              <a
                v-if="centralSettingsHref"
                class="custom-link"
                :href="centralSettingsHref"
                rel="noopener"
                target="_blank"
              >{{ $gt('Zentrale Einstellung') }}</a>
            </p>
          </template>
        </section>
        <v-checkbox
          v-model="aiEnabled"
          color="primary"
          density="comfortable"
          hide-details
          :label="$gt('Aufzeichnung & KI-Zusammenfassung anfragen')"
        />
        <p v-if="aiEnabled" class="text-caption text-medium-emphasis mt-1 ml-8">
          {{ $gt('Nach dem Verbinden wird der Gesprächspartner gefragt, ob er mit der Aufzeichnung und automatischen Zusammenfassung einverstanden ist. Lehnt er ab, erhalten Sie einen Hinweis zum manuellen Mitschreiben – das Gespräch findet ohne Aufzeichnung statt.') }}
        </p>
        <p v-else class="text-caption text-medium-emphasis mt-1 ml-8">
          {{ $gt('Ohne Aufzeichnung wird das Gespräch nicht transkribiert. Sie können die Aufzeichnung pro Anruf einzeln aktivieren – die Standard-Einstellung ist „aus&quot;.') }}
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="onCancel">{{ $gt('Abbrechen') }}</v-btn>
        <v-btn color="primary" variant="flat" :loading="callStore.dialing" @click="onConfirm">
          {{ $gt('Anrufen') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import {
  getCallerOptions,
  saveCallerFavorite,
  type CallerIdMode,
  type CallerOptions,
  type InitiateCallPayload,
} from "@/services/telephonyApi";
import { useAuthStore } from "@/stores/authStore";
import { useCallStore } from "@/stores/callStore";
import {
  callerModeForCall,
  canOpenCentralSettings,
  centralSettingsRoute,
  initialCallerMode,
  isFavoriteChoice,
} from "./callerIdChoice";

const callStore = useCallStore();
const authStore = useAuthStore();
const router = useRouter();
const aiEnabled = ref(false);
const callerOptions = ref<CallerOptions | null>(null);
const callerIdMode = ref<CallerIdMode | null>(null);
const saveAsFavorite = ref(false);

const pending = computed(() => callStore.pendingCall);
const isOpen = computed(() => !!pending.value);
const isFavorite = computed(() => isFavoriteChoice(callerOptions.value, callerIdMode.value));
// Eigener Tab, damit der vorbereitete Anruf nicht verloren geht.
const centralSettingsHref = computed(() => {
  if (!callerOptions.value || !canOpenCentralSettings(authStore.userRole)) return null;
  return router.resolve(centralSettingsRoute(callerOptions.value.idCompany)).href;
});

async function loadCallerOptions(call: InitiateCallPayload) {
  try {
    const options = await getCallerOptions(call.idCase);
    if (pending.value !== call) return; // Dialog inzwischen geschlossen oder neuer Anruf
    callerOptions.value = options;
    callerIdMode.value = initialCallerMode(options);
  } catch (e) {
    // Ladefehler verhindern den Anruf nicht: ohne Auswahl wie bisher.
    console.warn("Auswahl der Anzeige-Nummer nicht ladbar", e);
  }
}

watch(pending, (val) => {
  callerOptions.value = null;
  callerIdMode.value = null;
  saveAsFavorite.value = false;
  if (!val) return;
  // Reset opt-in to "aus" jedes Mal, wenn ein neuer Anruf vorbereitet wird.
  aiEnabled.value = false;
  loadCallerOptions(val);
});

function onCancel() {
  callStore.cancelPendingCall();
}

async function onConfirm() {
  const mode = callerModeForCall(callerOptions.value, callerIdMode.value);
  if (mode && saveAsFavorite.value && !isFavorite.value) {
    saveCallerFavorite(mode).catch((e) => console.warn("Favorit nicht gespeichert", e));
  }
  await callStore.confirmPendingCall(aiEnabled.value, mode);
}
</script>
